import { useLayoutEffect, useState } from 'react';

import { useStableCallback } from 'core/hooks/useStableCallback';

type TPosition = {
  [key in 'left' | 'top' | 'bottom' | 'right']?: string;
};

export default function usePosition(
  anchorNode: SVGElement | HTMLElement,
  close: () => void
) {
  const [pos, setPos] = useState<TPosition>({});
  const [popup, setPopup] = useState<HTMLElement | null>(null);

  const reposition = useStableCallback((): void => {
    if (!popup) return;

    const box = anchorNode.getBoundingClientRect();
    const w = popup.offsetWidth;
    const h = popup.offsetHeight;
    const viewport = {
      w: window.innerWidth,
      h: window.innerHeight,
    };

    const pos: TPosition = {};

    if (box.bottom + PADDING + h + PADDING < viewport.h) {
      pos.top = box.bottom + PADDING + 'px';
    } else {
      pos.bottom = viewport.h - box.top + PADDING + 'px';
    }

    if (box.right - w > 0) {
      pos.right = viewport.w - box.right + 'px';
    } else {
      pos.left = Math.min(box.left, viewport.w - PADDING - w) + 'px';
    }

    setPos(pos);
  });

  useLayoutEffect(() => {
    reposition();

    const { top } = anchorNode.getBoundingClientRect();
    const onScroll = () => {
      if (
        Math.abs(anchorNode.getBoundingClientRect().top - top) > SCROLL_DELTA
      ) {
        close();
      } else {
        reposition();
      }
    };

    const onClick = (evt: Event) => {
      const target = evt.target as HTMLElement;
      if (!anchorNode.contains(target) && !popup?.contains(target)) {
        close();
      }
    };

    window.addEventListener('resize', reposition);
    document.body.addEventListener('scroll', onScroll, { capture: true });
    document.body.addEventListener('mousedown', onClick, { capture: true });

    return () => {
      window.removeEventListener('resize', reposition);
      document.body.removeEventListener('scroll', onScroll, { capture: true });
      document.body.removeEventListener('mousedown', onClick, {
        capture: true,
      });
    };
  }, [popup, anchorNode]);

  return { pos, setPopup };
}

const PADDING = 4;
const SCROLL_DELTA = 5; // px
